import { createSelector } from '@reduxjs/toolkit';

import {
  AchDetails,
  CreditCard,
  DdDetails,
  PaymentMethodLimits,
  PaymentMethodsState,
  PaypalAgreements,
  RazorPayAccounts,
  SortedPaymentMethodList,
} from 'types/payment-method';
import { RootState } from 'types/root';
import { PAYMENT_METHOD_TYPES, PAYMENT_METHODS_TYPES } from 'utils/constants';

export const paymentMethodsTree = (state: RootState): PaymentMethodsState => state.paymentMethods || {};

export const paymentMethodsIsLoading = (state: RootState): boolean => paymentMethodsTree(state).isLoading || false;

// ------------------------------------
// Payment lists
// ------------------------------------
export const creditCardList = createSelector([paymentMethodsTree], (paymentMethods): CreditCard[] => [
  ...paymentMethods.creditCards.map((card) => ({ ...card, isAdyen: false })),
  ...paymentMethods.creditCardsAdyen.map((card) => ({ ...card, isAdyen: true })),
]);

export const achList = createSelector([paymentMethodsTree], (paymentMethods): AchDetails[] => [
  ...paymentMethods.ach.map((ach) => ({ ...ach, isAdyen: false })),
  ...paymentMethods.achBankAccountsAdyen.map((ach) => ({ ...ach, isAdyen: true })),
]);

export const directDebitList = (state: RootState): DdDetails[] => paymentMethodsTree(state).directDebits || [];

export const paypalList = (state: RootState): PaypalAgreements[] => paymentMethodsTree(state).payPalAgreements || [];

export const razorPayList = (state: RootState): RazorPayAccounts[] => paymentMethodsTree(state).razorPayAccounts || [];

export const sortedPaymentMethodList = createSelector(
  [creditCardList, achList, directDebitList, paypalList, razorPayList],
  (creditCards, ach, directDebits, payPalAgreements, razorPayAccounts) => {
    const acc: SortedPaymentMethodList[] = [];

    creditCards.forEach((payment) => acc.push({ ...payment, paymentMethodType: PAYMENT_METHODS_TYPES.cc }));
    ach.forEach((payment) => acc.push({ ...payment, paymentMethodType: PAYMENT_METHODS_TYPES.ach }));
    directDebits.forEach((payment) => acc.push({ ...payment, paymentMethodType: PAYMENT_METHODS_TYPES.dd }));
    payPalAgreements.forEach((payment) => acc.push({ ...payment, paymentMethodType: PAYMENT_METHODS_TYPES.paypal }));
    razorPayAccounts.forEach((payment) => acc.push({ ...payment, paymentMethodType: PAYMENT_METHODS_TYPES.razorpay }));
    return acc.sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : 0));
  },
);

export const isPaymentMethodPresent = createSelector(
  [creditCardList, achList, directDebitList, paypalList, razorPayList],
  (cc, ach, dd, paypal, razorpay): boolean =>
    !!cc.length || !!ach.length || !!dd.length || !!paypal.length || !!razorpay.length,
);

// ------------------------------------
// Payment limits
// ------------------------------------
export const paymentMethodLimits = (state: RootState): PaymentMethodLimits =>
  paymentMethodsTree(state).paymentMethodLimits;

export const paymentMethodsAdditionCount = createSelector(
  [paymentMethodLimits, creditCardList, achList, directDebitList, paypalList, razorPayList],
  (paymentMethodLimits, creditCards, ach, directDebits, payPalAgreements, razorPayAccounts) => ({
    [PAYMENT_METHODS_TYPES.cc]: paymentMethodLimits.CREDIT_CARD - creditCards.length,
    [PAYMENT_METHODS_TYPES.ach]: paymentMethodLimits.ACH - ach.length,
    [PAYMENT_METHODS_TYPES.dd]: paymentMethodLimits.DirectDebit - directDebits.length,
    [PAYMENT_METHODS_TYPES.paypal]: paymentMethodLimits.PayPal - payPalAgreements.length,
    [PAYMENT_METHODS_TYPES.razorpay]: paymentMethodLimits.RazorPay - razorPayAccounts.length,
  }),
);

// ------------------------------------
// Loaders
// ------------------------------------
export const isDeletePaymentMethodLoading = (state: RootState): boolean =>
  paymentMethodsTree(state).isDeletePaymentMethodLoading || false;

export const isSetDefaultPaymentLoading = (state: RootState): boolean =>
  paymentMethodsTree(state).isSetDefaultPaymentLoading || false;

export const paymentTypesIsLoading = (state: RootState): boolean =>
  paymentMethodsTree(state).isPaymentTypeLoading || false;

// ------------------------------------
// Allowed payments
// ------------------------------------
export const paymentMethodTypes = (state: RootState): string[] => paymentMethodsTree(state).paymentMethodTypes || [];

export const isAddPaymentMethodSupported = (state: RootState): boolean => {
  const addPaymentMethodCount = paymentMethodsAdditionCount(state);
  return Object.values(addPaymentMethodCount).some((count) => count > 0);
};

export const directDebitType = (state: RootState): string =>
  paymentMethodTypes(state).find((type) => PAYMENT_METHOD_TYPES[PAYMENT_METHODS_TYPES.dd].includes(type)) || '';

// ------------------------------------
// UI state
// ------------------------------------
export const chosenPaymentMethodKey = (state: RootState): string =>
  paymentMethodsTree(state).chosenPaymentMethodKey || sortedPaymentMethodList(state)[0]?.paymentMethodKey || '';

export const payNowIntent = (state: RootState): string => paymentMethodsTree(state).payNowIntent || '';
